import React from "react";
import { toast } from "react-toastify";
import { getRolePath } from "./common";
import { playMessageNotificationSound } from "./notificationSound";

export function isMessageNotification(notifyData) {
  const type = String(notifyData?.notification_type || "").toLowerCase();
  return type === "message" || type === "messages";
}

export function isDocumentOrFolderNotification(notifyData) {
  const type = String(notifyData?.notification_type || "").toLowerCase();
  return type === "document" || type === "folder";
}

/** Normalize business vs individual from notification or API payload. */
export function normalizeDocumentCategoryFromNotification(notifyData) {
  const raw =
    notifyData?.document_category ??
    notifyData?.documentCategory ??
    notifyData?.category ??
    "";
  const s = String(raw).trim().toLowerCase();
  if (s === "business" || s.startsWith("business")) return "business";
  return "individual";
}

/** Client details tab id for the document category. */
export function getClientDocumentsActiveTab(documentCategory) {
  return "documents";
}

/** Sent / received / office / filed_copies / k1_package sub-tab within documents UI. */
export function getDocumentsFileTypeFromNotification(notifyData) {
  const docType = String(notifyData?.doc_type ?? "").toLowerCase();
  if (docType === "k1_package") return "k1_package";
  if (docType === "filed_copies") return "filed_copies";
  if (docType === "office") return "office";
  const tab = String(notifyData?.documents_tab ?? notifyData?.documentsTab ?? "")
    .trim()
    .toLowerCase();
  if (
    tab === "sent" ||
    tab === "received" ||
    tab === "office" ||
    tab === "requested" ||
    tab === "k1_package" ||
    tab === "filed_copies"
  ) {
    return tab;
  }
  if (docType === "emp") return "received";
  if (docType === "client") return "received";
  return "received";
}

/**
 * Client id for a document/folder upload notification.
 * Staff: initiated_user_id is the client whose documents were updated.
 */
export function getDocumentNotificationClientId(notifyData, userRole) {
  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();
  const d = notifyData || {};

  if (role === "client") {
    return localStorage.getItem("user_id") || d.client_id || d.initiated_user_id;
  }

  return (
    d.initiated_user_id ||
    d.client_id ||
    d.notification_client_id ||
    d.user_id ||
    null
  );
}

/**
 * Route for document/folder upload notifications.
 * Staff → client_details documents tab; client → documents page.
 */
export function getDocumentNotificationRoute(notifyData, userRole) {
  if (!isDocumentOrFolderNotification(notifyData)) return null;

  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();
  const clientId = getDocumentNotificationClientId(notifyData, role);
  if (!clientId) return null;

  const documentCategory = normalizeDocumentCategoryFromNotification(notifyData);
  const documentsTab = getDocumentsFileTypeFromNotification(notifyData);
  const notificationType = String(
    notifyData?.notification_type || "",
  ).toLowerCase();
  const itemId = notifyData?.notification_item_id;

  const state = {
    fromNotification: true,
    clientId,
    documentCategory,
    documentsTab,
  };

  if (notificationType === "folder") {
    state.openFolder = true;
    state.folderId = itemId;
  } else {
    state.openDocument = true;
    state.documentId = itemId;
  }

  if (role === "client") {
    return {
      path: getRolePath("/documents"),
      state,
    };
  }

  return {
    path: getRolePath(`/client_details/${clientId}`),
    state: {
      ...state,
      tab: getClientDocumentsActiveTab(documentCategory),
    },
  };
}

/**
 * Resolve client thread id from a message notification payload.
 * For clients, always use the logged-in user (never initiated_user_id — that is staff).
 */
export function getMessageNotificationClientId(notifyData, userRole) {
  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();
  const d = notifyData || {};

  if (role === "client") {
    return localStorage.getItem("user_id") || d.client_id || null;
  }

  return (
    d.client_id ||
    d.initiated_user_id ||
    d.notification_item_id ||
    null
  );
}

function parseClientNameFromMessage(message = "") {
  const text = String(message || "").trim();
  if (!text) return null;

  const patterns = [
    /^New message from (.+?):\s*.+$/i,
    /^(.+?) sent (?:you )?a message:?\s*.+$/i,
    /^Message from (.+?):\s*.+$/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return null;
}

function parseMessageBodyFromNotification(message = "") {
  const text = String(message || "").trim();
  if (!text) return "New message";

  const splitPatterns = [
    /^New message from .+?:\s*(.+)$/i,
    /^.+? sent (?:you )?a message:?\s*(.+)$/i,
    /^Message from .+?:\s*(.+)$/i,
  ];

  for (const pattern of splitPatterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].trim();
  }

  return text;
}

/** Client/sender label + message preview for message notification UI. */
export function getMessageNotificationDisplay(notifyData) {
  const d = notifyData || {};
  const rawMessage = String(d.message || d.message_text || "").trim();

  const clientName =
    d.client_name ||
    d.initiated_user_name ||
    d.sender_name ||
    d.user_name ||
    (d.bill_to_first_name || d.bill_to_last_name
      ? `${d.bill_to_first_name || ""} ${d.bill_to_last_name || ""}`.trim()
      : null) ||
    parseClientNameFromMessage(rawMessage) ||
    (String(localStorage.getItem("user_role") || "").toLowerCase() === "client"
      ? "Your Associate"
      : "Client");

  const messageText = parseMessageBodyFromNotification(rawMessage);

  return { clientName, messageText };
}

/**
 * Route + state for opening the messages UI from a message notification.
 * Clients → /messages; staff → client_details with messages tab.
 */
export function getMessageNotificationRoute(notifyData, userRole) {
  const role = String(
    userRole || localStorage.getItem("user_role") || "",
  ).toLowerCase();
  const clientId = getMessageNotificationClientId(notifyData, role);

  if (role === "client") {
    if (!clientId) return null;
    return {
      path: getRolePath("/messages"),
      state: { client_id: clientId },
    };
  }

  if (!clientId) return null;

  return {
    path: getRolePath(`/client_details/${clientId}`),
    state: { tab: "messages" },
  };
}

const MSG_NOTIF_INIT_KEY = "finlo_msg_notif_initialized";
const MSG_NOTIF_TOASTED_KEY = "finlo_msg_notif_toasted";

function readToastedNotificationIds() {
  try {
    const raw = sessionStorage.getItem(MSG_NOTIF_TOASTED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeToastedNotificationIds(ids) {
  sessionStorage.setItem(
    MSG_NOTIF_TOASTED_KEY,
    JSON.stringify([...ids]),
  );
}

export function wasMessageNotificationToasted(notificationId) {
  const id = String(notificationId ?? "");
  if (!id) return false;
  return readToastedNotificationIds().has(id);
}

export function markMessageNotificationToasted(notificationId) {
  const id = String(notificationId ?? "");
  if (!id) return;
  const ids = readToastedNotificationIds();
  ids.add(id);
  writeToastedNotificationIds(ids);
}

/** Mark existing notifications as already toasted (first load / new session). */
export function seedMessageNotificationToasts(notifications) {
  if (sessionStorage.getItem(MSG_NOTIF_INIT_KEY)) return false;

  const list = Array.isArray(notifications) ? notifications : [];
  if (list.length === 0) return false;

  const ids = readToastedNotificationIds();
  list.forEach((item) => {
    const id = String(item?.notification_id ?? "");
    if (id) ids.add(id);
  });
  writeToastedNotificationIds(ids);
  sessionStorage.setItem(MSG_NOTIF_INIT_KEY, "1");
  return true;
}

export function clearMessageNotificationToastState() {
  sessionStorage.removeItem(MSG_NOTIF_INIT_KEY);
  sessionStorage.removeItem(MSG_NOTIF_TOASTED_KEY);
}

/** Show a clickable toast for a new message notification. */
export function showMessageNotificationToast(notifyData, onClick) {
  if (!notifyData?.notification_id) return;

  const notificationId = String(notifyData.notification_id);
  if (wasMessageNotificationToasted(notificationId)) return;

  markMessageNotificationToasted(notificationId);

  const { clientName, messageText } = getMessageNotificationDisplay(notifyData);
  const toastId = `msg-notif-${notificationId}`;
  const avatarInitial =
    String(clientName || "U").trim().charAt(0).toUpperCase() || "U";

  if (toast.isActive(toastId)) return;

  playMessageNotificationSound();

  toast(
    React.createElement(
      "div",
      { className: "message-notification-toast" },
      React.createElement(
        "div",
        { className: "message-notification-toast-row" },
        React.createElement(
          "div",
          {
            className: "avatar-circle message-notification-toast-avatar",
            "aria-hidden": true,
          },
          avatarInitial,
        ),
        React.createElement(
          "div",
          { className: "message-notification-toast-content" },
          React.createElement(
            "div",
            { className: "message-notification-toast-title" },
            clientName,
          ),
          React.createElement(
            "div",
            { className: "message-notification-toast-body" },
            messageText,
          ),
        ),
      ),
    ),
    {
      toastId,
      type: "default",
      icon: false,
      autoClose: 10000,
      closeOnClick: false,
      hideProgressBar: false,
      className: "message-notification-toast-container",
      style: { cursor: "pointer" },
      onClick: () => {
        toast.dismiss(toastId);
        if (onClick) onClick(notifyData);
      },
    },
  );
}

/** Detect unread message notifications that have not been toasted yet. */
export function collectNewMessageNotifications(notifications) {
  const list = Array.isArray(notifications) ? notifications : [];
  const toastedIds = readToastedNotificationIds();
  const fresh = [];

  list.forEach((item) => {
    if (!isMessageNotification(item)) return;
    if (item.view_status !== "Not Viewed") return;

    const id = String(item.notification_id ?? "");
    if (!id || toastedIds.has(id)) return;

    fresh.push(item);
  });

  return fresh;
}
